import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Link,
  ListItem,
  Spacer,
  Spinner,
  Switch,
  Tag,
  Text,
  Tooltip,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { BsPencilSquare, BsPlus } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteJobWebhookConfigMutation,
  useGetJobWebhookConfigsQuery,
  useModalitiesQuery,
  useRestoreJobWebhookConfigMutation,
} from '@/app/services/nucleus';
import DataTable from '@/features/common/table/DataTable';
import TableButton from '../common/table/TableButton';
import TruncatedArrayComponent from '@/features/common/TruncatedArrayComponent';
import AlertComponent from '../common/AlertComponent';
import JobFeedModal from './JobFeedModal';
import { idsToModalities, jobFeedErrorMessage } from './jobFeedHelpers';

const JobFeedPage = () => {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [togglingId, setTogglingId] = useState(null);

  const { data: configs, isLoading, isError, error } = useGetJobWebhookConfigsQuery();
  const { data: modalitiesData } = useModalitiesQuery();

  const [deleteConfig] = useDeleteJobWebhookConfigMutation();
  const [restoreConfig] = useRestoreJobWebhookConfigMutation();

  const modalities = useMemo(() => modalitiesData?.items || [], [modalitiesData?.items]);

  // The v1 endpoint returns a bare array, but normalize defensively in case the contract
  // ever returns a paginated { items } shape. Memoized so DataTable gets a stable reference.
  const rows = useMemo(() => (Array.isArray(configs) ? configs : configs?.items ?? []), [configs]);

  const handleEdit = (id) => navigate(`/job_feed/${id}`);

  const handleToggleActive = async (config) => {
    setTogglingId(config.id);
    try {
      if (config.is_deleted) {
        await restoreConfig(config.id).unwrap();
      } else {
        await deleteConfig(config.id).unwrap();
      }
    } catch (err) {
      console.error('Error toggling job feed config', err);
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('url', {
      header: 'URL',
      cell: ({ getValue }) => (
        <Link href={getValue()} isExternal color="brand.700" wordBreak="break-all">
          {getValue()}
        </Link>
      ),
    }),
    columnHelper.accessor('modality_ids', {
      header: 'Modalities',
      disableSortBy: true,
      cell: ({ getValue }) => {
        const items = idsToModalities(getValue() || [], modalities);
        if (items.length === 0) {
          return <Text color="gray.500">All modalities</Text>;
        }
        const truncatedItems = items.slice(0, 3);
        const truncatedString = truncatedItems.map((m) => m.short_name).join(', ');
        const row = { modalitiesPopover: items, modalitiesTruncated: truncatedString, modalitiesTruncatedList: truncatedItems };
        return (
          <TruncatedArrayComponent
            title="Modalities"
            fieldName="modalities"
            row={row}
            renderTruncated={(list) =>
              list.map((m) => (
                <Tag key={m.id} size="sm" colorScheme="purple" borderRadius="full">
                  {m.short_name}
                </Tag>
              ))
            }
            renderItems={(list) => (
              <UnorderedList>
                {list.map((m) => (
                  <ListItem key={m.id}>{m.short_name}</ListItem>
                ))}
              </UnorderedList>
            )}
          />
        );
      },
    }),
    columnHelper.accessor('is_deleted', {
      header: 'Status',
      disableSortBy: true,
      cell: ({ getValue }) => <Badge colorScheme={getValue() ? 'gray' : 'green'}>{getValue() ? 'Inactive' : 'Active'}</Badge>,
    }),
    columnHelper.accessor('', {
      header: 'Action',
      disableSortBy: true,
      cell: (info) => {
        const config = info.row.original;
        return (
          <HStack spacing={3}>
            <Tooltip hasArrow placement="top" label="Edit Job Feed">
              <Box p="0" as="span">
                <TableButton aria-label="edit" icon={<BsPencilSquare />} onClick={() => handleEdit(config.id)} />
              </Box>
            </Tooltip>
            <Tooltip hasArrow placement="top" label={config.is_deleted ? 'Activate' : 'Deactivate'}>
              <Box p="0" as="span">
                <Switch
                  aria-label={`toggle-active-${config.id}`}
                  colorScheme="purple"
                  isChecked={!config.is_deleted}
                  isDisabled={togglingId === config.id}
                  onChange={() => handleToggleActive(config)}
                />
              </Box>
            </Tooltip>
          </HStack>
        );
      },
    }),
  ];

  return (
    <Box data-test-id="pact-job-feed-page">
      <Flex alignItems="center">
        <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
          Job Feed
        </Heading>
        <Spacer />
        <Button leftIcon={<BsPlus />} onClick={onOpen} variant="purple" type="button">
          New
        </Button>
      </Flex>
      <Text color="gray.500" fontSize="sm" mt={2}>
        Manage which external job boards receive job webhooks.
      </Text>

      <Box mt={5}>
        {isError ? (
          <AlertComponent status="warning" title="Job Feed Error" description={jobFeedErrorMessage(error, 'Error fetching job feeds')} />
        ) : isLoading ? (
          <Flex alignItems="center" justifyContent="center" py={20}>
            <Spinner color="brand.900" size="lg" />
          </Flex>
        ) : (
          <DataTable loading={isLoading} data={rows} columns={columns} sortBy="name" sortDirection="asc" showScrollbar />
        )}
      </Box>

      <JobFeedModal isOpen={isOpen} onClose={onClose} onSave={onClose} />
    </Box>
  );
};

export default JobFeedPage;
